/**
 * Cohort-related configuration constants
 * Centralized to avoid duplication across components
 */

import type { ReactNode } from 'react';
import { createElement } from 'react';
import { Users, User, Briefcase } from 'react-feather';
import type {
  SessionFrequency,
  PreferredDay,
  PreferredTime,
  ModalityType,
  WorkshopPattern,
  ParticipantAssignment,
  MeetingPlatform,
  CoachingSubType,
} from '../types';

// ========================
// MODALITY CONFIGURATION
// ========================

// Modality icons for consistent display across components
export const MODALITY_ICONS: Record<ModalityType, (size?: number) => ReactNode> = {
  group_coaching: (size = 18) => createElement(Users, { size }),
  one_on_one: (size = 18) => createElement(User, { size }),
  workshop: (size = 18) => createElement(Briefcase, { size }),
};

// Modality options for selection
export const MODALITY_OPTIONS: readonly {
  value: ModalityType;
  label: string;
  description: string;
  icon?: string;
}[] = [
  {
    value: 'group_coaching',
    label: 'Group Coaching',
    description: 'Small group sessions led by a coach with 8-12 participants per group',
  },
  {
    value: 'one_on_one',
    label: '1:1 Coaching',
    description: 'Individual coaching sessions between a coach and participant',
  },
  {
    value: 'workshop',
    label: 'Workshops',
    description: 'Larger format interactive sessions focused on skill-building',
  },
];

// Modality labels for display
export const MODALITY_LABELS: Record<ModalityType, string> = {
  group_coaching: 'Group Coaching',
  one_on_one: '1:1 Coaching',
  workshop: 'Workshops',
};

// Workshop pattern options
export const WORKSHOP_PATTERN_OPTIONS: readonly {
  value: WorkshopPattern;
  label: string;
  description: string;
}[] = [
  {
    value: 'parallel',
    label: 'Parallel (Standalone)',
    description: 'Workshops can be attended in any order',
  },
  {
    value: 'sequential',
    label: 'Sequential (Ordered)',
    description: 'Workshops must be attended in sequence',
  },
];

// Workshop pattern labels for display
export const WORKSHOP_PATTERN_LABELS: Record<WorkshopPattern, string> = {
  parallel: 'Parallel (Standalone)',
  sequential: 'Sequential (Ordered)',
};

// Participant assignment options
export const PARTICIPANT_ASSIGNMENT_OPTIONS: readonly {
  value: ParticipantAssignment;
  label: string;
  description: string;
}[] = [
  {
    value: 'all_participants',
    label: 'All Participants',
    description: 'All cohort participants attend this workshop',
  },
  {
    value: 'subset',
    label: 'Subset',
    description: 'Only a subset of participants attend this workshop',
  },
];

// Participant assignment labels for display
export const PARTICIPANT_ASSIGNMENT_LABELS: Record<ParticipantAssignment, string> = {
  all_participants: 'All Participants',
  subset: 'Subset',
};

// ========================
// MED-848 — COACHING SUB-TYPE & AI FEATURES
// ========================

export const COACHING_SUB_TYPE_OPTIONS: readonly {
  value: CoachingSubType;
  label: string;
  description: string;
}[] = [
  {
    value: 'coach_led',
    label: 'Coach led',
    description: 'Sessions led by an assigned coach',
  },
  {
    value: 'peer_led',
    label: 'Peer led',
    description: 'Sessions led by a peer leader designated from participants',
  },
];

export const COACHING_SUB_TYPE_LABELS: Record<CoachingSubType, string> = {
  coach_led: 'Coach led',
  peer_led: 'Peer led',
};

// ========================
// LIVE SESSION DELIVERY (MED-948 / E1.10a)
// ========================
// Shared copy for the per-cohort Live Session Delivery gate. Centralized so the
// Create Cohort wizard and the Edit Cohort modal render identical wording — the
// label is a "working label" the client may revise; keep it in one place.

// AC1/AC4 — label shown on the toggle in both the create wizard and edit modal.
export const LIVE_SESSION_DELIVERY_LABEL = 'Live Session Delivery in SGOS';

// AC1 — info tooltip accompanying the toggle.
export const LIVE_SESSION_DELIVERY_TOOLTIP =
  "Turn on if this cohort's group sessions will be fully delivered in the SGOS platform.";

// AC8 — confirmation prompt copy shown when turning the gate off while AI
// features are currently enabled. Confirming resets AI features to off.
export const LIVE_SESSION_DELIVERY_OFF_CONFIRM =
  'Turning off Live Session Delivery in SGOS will disable AI features for this cohort. This change will apply to all future sessions. Are you sure you want to continue?';

// ========================
// MODALITY CONFIG PILLS (MED-1038 / E1.10d)
// ========================
// Labels for the read-only facilitation-config pills shown on modality headers
// across the Overview and Sessions tabs. "Live Delivery" is the compact on-pill
// form of the "Live Session Delivery" cohort setting — kept short so multiple
// pills fit the collapsed All-view header without crowding. Centralized so the
// single ModalityConfigPills component renders identical labels in all four
// locations (AC13).
export const MODALITY_CONFIG_PILL_LABELS = {
  liveDelivery: 'Live Delivery',
  aiEnabled: 'AI Enabled',
  aiOff: 'AI Off',
  // MED-1069 (E1.10c): compact on-pill form of the "Commitment Tracking" group
  // coaching setting — shown only when enabled, mirroring "Live Delivery".
  commitments: 'Commitments',
} as const;

// ========================
// SESSION CONFIGURATION
// ========================

// Session count options
export const SESSION_COUNTS = [4, 6, 8] as const;

export const SESSION_COUNT_OPTIONS = [
  { label: '4 Sessions', value: '4' },
  { label: '6 Sessions', value: '6' },
  { label: '8 Sessions', value: '8' },
] as const;

// Session duration options (in minutes)
export const SESSION_DURATION_OPTIONS: readonly { value: number; label: string }[] = [
  { value: 30, label: '30 min' },
  { value: 45, label: '45 min' },
  { value: 60, label: '60 min' },
  { value: 75, label: '75 min' },
  { value: 90, label: '90 min' },
  { value: 120, label: '120 min' },
];

// 1:1 coaching duration options (AC7-8: 45 and 60 min only)
export const ONE_ON_ONE_DURATION_OPTIONS: readonly { value: number; label: string }[] = [
  { value: 45, label: '45 min' },
  { value: 60, label: '60 min' },
];

// Default session durations by modality type
export const DEFAULT_SESSION_DURATION: Record<string, number> = {
  group_coaching: 90,
  one_on_one: 60,
  workshop: 60,
};

// Frequency options
export const FREQUENCY_OPTIONS: readonly { value: SessionFrequency; label: string }[] = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'biweekly', label: 'Biweekly' },
  { value: 'monthly', label: 'Monthly' },
];

// Frequency labels for display
export const FREQUENCY_LABELS: Record<SessionFrequency, string> = {
  weekly: 'Weekly',
  biweekly: 'Biweekly',
  monthly: 'Monthly',
};

// Frequency in days for calculations
export const FREQUENCY_DAYS: Record<SessionFrequency, number> = {
  weekly: 7,
  biweekly: 14,
  monthly: 28,
};

// Day options
export const DAY_OPTIONS: readonly { value: PreferredDay; label: string }[] = [
  { value: 'mon', label: 'Mon' },
  { value: 'tue', label: 'Tue' },
  { value: 'wed', label: 'Wed' },
  { value: 'thu', label: 'Thu' },
  { value: 'fri', label: 'Fri' },
];

// Day labels for display
export const DAY_LABELS: Record<PreferredDay, string> = {
  mon: 'Mon',
  tue: 'Tue',
  wed: 'Wed',
  thu: 'Thu',
  fri: 'Fri',
};

// Map day abbreviations to JS day numbers (0 = Sunday, 1 = Monday, etc.)
export const DAY_TO_NUMBER: Record<PreferredDay, number> = {
  mon: 1,
  tue: 2,
  wed: 3,
  thu: 4,
  fri: 5,
};

// Time options
export const TIME_OPTIONS: readonly { value: PreferredTime; label: string; description: string }[] = [
  { value: 'morning', label: 'Morning', description: '8am-12pm' },
  { value: 'afternoon', label: 'Afternoon', description: '12pm-6pm' },
  { value: 'evening', label: 'Evening', description: '6pm-9pm' },
];

// Time options with times for modals (slightly different format)
export const TIME_OPTIONS_WITH_TIMES: readonly { value: PreferredTime; label: string }[] = [
  { value: 'morning', label: 'Morning (9 AM)' },
  { value: 'afternoon', label: 'Afternoon (1 PM)' },
  { value: 'evening', label: 'Evening (6 PM)' },
];

// Time labels for display
export const TIME_LABELS: Record<PreferredTime, string> = {
  morning: 'Morning',
  afternoon: 'Afternoon',
  evening: 'Evening',
};

// ========================
// MEETING PLATFORM CONFIGURATION
// ========================

export const MEETING_PLATFORM_OPTIONS: readonly { value: MeetingPlatform; label: string }[] = [
  { value: 'zoom', label: 'Zoom' },
  { value: 'teams', label: 'Microsoft Teams' },
  { value: 'google_meet', label: 'Google Meet' },
  { value: 'in_person', label: 'In Person' },
];

export const MEETING_PLATFORM_LABELS: Record<MeetingPlatform, string> = {
  zoom: 'Zoom',
  teams: 'Microsoft Teams',
  google_meet: 'Google Meet',
  in_person: 'In Person',
};

